import React from 'react';
import { motion } from 'framer-motion';
import Hero from '../components/ui/Hero';
import Section from '../components/ui/Section';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiMusic, FiHeart, FiUsers, FiCalendar, FiClock } = FiIcons;

const CityOutreach = () => {
  const outreachTypes = [
    {
      icon: FiUsers,
      title: 'Street Evangelism',
      description: 'Engaging people in downtown areas, parks, and public spaces with the gospel message through authentic conversations.',
      locations: 'Downtown cores, university areas, popular gathering spots'
    },
    {
      icon: FiHeart,
      title: 'Prayer Walks',
      description: 'Walking through neighborhoods and business districts, praying for the community and looking for divine appointments.',
      locations: 'Residential areas, business districts, college towns'
    },
    {
      icon: FiMusic,
      title: 'Worship Nights',
      description: 'Public worship events in parks and squares, creating an atmosphere for God to move in people\'s hearts.',
      locations: 'City parks, public squares, beach areas'
    },
    {
      icon: FiMapPin,
      title: 'Community Events',
      description: 'Participating in local festivals and events to build relationships and share the hope of Christ.',
      locations: 'Festivals, farmers markets, community gatherings'
    }
  ];

  const cities = [
    {
      name: 'Gainesville',
      population: '141,000',
      focus: 'University district and downtown core',
      frequency: 'Weekly outreach'
    },
    {
      name: 'Tallahassee',
      population: '194,000',
      focus: 'Capitol area and student neighborhoods',
      frequency: 'Bi-weekly events'
    },
    {
      name: 'Orlando',
      population: '307,000',
      focus: 'Downtown and tourist corridors',
      frequency: 'Monthly initiatives'
    },
    {
      name: 'Miami',
      population: '467,000',
      focus: 'Beach areas and cultural districts',
      frequency: 'Monthly outreach'
    },
    {
      name: 'Tampa',
      population: '399,000',
      focus: 'Riverwalk and business district',
      frequency: 'Bi-weekly presence'
    },
    {
      name: 'Jacksonville',
      population: '949,000',
      focus: 'Downtown and riverside areas',
      frequency: 'Monthly events'
    }
  ];

  const stories = [
    {
      title: 'Downtown Encounter',
      location: 'Gainesville',
      story: 'A young professional stopped to listen during our worship night in the square. Through tears, she shared how the music reminded her of her childhood faith. She gave her life back to Jesus that evening.',
      impact: 'Life transformation'
    },
    {
      title: 'Street Corner Miracle',
      location: 'Orlando',
      story: 'While doing street evangelism, we met a man struggling with addiction. After prayer and conversation, he committed to rehab and accepted Christ. He\'s now 8 months sober and serving in ministry.',
      impact: 'Addiction recovery & salvation'
    },
    {
      title: 'Prayer Walk Breakthrough',
      location: 'Tampa',
      story: 'During a prayer walk through a struggling neighborhood, we were invited into a home to pray for a sick child. The family experienced healing and the entire household came to faith.',
      impact: 'Family salvation & healing'
    }
  ];

  return (
    <div>
      <Hero
        title="City Outreach"
        description="Taking the gospel beyond campus walls into the heart of Florida's cities. Through street evangelism, prayer walks, and worship events, we're seeing God transform lives in urban centers across the state."
        primaryCta={{
          text: 'Join City Outreach',
          href: '/get-involved'
        }}
        secondaryCta={{
          text: 'Upcoming Events',
          href: '/events'
        }}
        backgroundImage="https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80"
      />

      {/* Outreach Methods */}
      <Section>
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            How We Reach Cities
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            Our city outreach combines bold evangelism with compassionate community engagement, meeting people where they are with the love of Christ.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {outreachTypes.map((type, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 h-full">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={type.icon} className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">{type.title}</h3>
                    <p className="text-neutral-600 mb-3">{type.description}</p>
                    <div className="text-sm text-primary-600">
                      <span className="font-medium">Locations:</span> {type.locations}
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Cities We Serve */}
      <Section background="gray">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            Cities We Impact
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            From small college towns to major metropolitan areas, Love 180 is bringing transformation to communities across Florida.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cities.map((city, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-6">
                <div className="flex items-center space-x-2 mb-3">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 text-primary-600" />
                  <h3 className="text-lg font-semibold">{city.name}</h3>
                </div>
                <div className="space-y-2 text-sm text-neutral-600">
                  <div>👥 {city.population} residents</div>
                  <div>🎯 {city.focus}</div>
                  <div>📅 {city.frequency}</div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-neutral-600 mb-6">Don't see your city? We'd love to start an outreach there!</p>
          <div className="flex justify-center">
            <Button variant="primary" size="lg">
              Request City Outreach
            </Button>
          </div>
        </div>
      </Section>

      {/* Transformation Stories */}
      <Section>
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            Stories of City Transformation
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            God is moving powerfully in Florida's cities. Here are just a few stories of how lives are being transformed through our outreach efforts.
          </p>
        </div>

        <div className="space-y-8">
          {stories.map((story, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Card className="p-6">
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{story.title}</h3>
                    <div className="flex items-center space-x-2 text-primary-600 mb-2">
                      <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                      <span className="font-medium">{story.location}</span>
                    </div>
                    <div className="bg-primary-50 px-3 py-1 rounded-full text-sm text-primary-700 inline-block">
                      {story.impact}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-neutral-700 leading-relaxed">{story.story}</p>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Monthly Schedule */}
      <Section background="gray">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-6">
              Monthly Outreach Calendar
            </h2>
            <p className="text-lg text-neutral-600 mb-6">
              We maintain a regular presence in cities across Florida with scheduled outreach events, worship nights, and prayer initiatives.
            </p>
            
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 text-primary-600" />
                  <span className="font-medium">First Friday</span>
                </div>
                <p className="text-neutral-600">Downtown worship nights in major cities</p>
              </div>
              
              <div className="bg-white p-4 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <SafeIcon icon={FiUsers} className="w-5 h-5 text-primary-600" />
                  <span className="font-medium">Every Saturday</span>
                </div>
                <p className="text-neutral-600">Street evangelism teams deploy to various locations</p>
              </div>
              
              <div className="bg-white p-4 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <SafeIcon icon={FiHeart} className="w-5 h-5 text-primary-600" />
                  <span className="font-medium">Mid-Month</span>
                </div>
                <p className="text-neutral-600">Community prayer walks in each target city</p>
              </div>
            </div>
          </div>
          
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1519452634265-7b808fcb7ad4?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
              alt="City outreach team"
              className="rounded-lg shadow-lg"
            />
          </div>
        </div>
      </Section>

      {/* Call to Action */}
      <Section>
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-6">
            Your City Needs Jesus
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto mb-8">
            Every city in Florida has people waiting for someone to share the hope of Christ with them. Join us as we take the gospel to the streets and see God transform entire communities.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="primary" size="lg" href="/get-involved">
              Join Our Outreach Team
            </Button>
            <Button variant="outline" size="lg" href="/events">
              Find Outreach Events Near You
            </Button>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default CityOutreach;